/**
 * Status Timeline
 * Visual timeline of plan status changes
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusTimeline = () => {
  const { planId } = useParams();
  const [history, setHistory] = useState([]);
  const [planInfo, setPlanInfo] = useState(null);

  useEffect(() => {
    if (planId) {
      fetchHistory();
      fetchPlanInfo();
    }
  }, [planId]);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/plan-status/${planId}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistory(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchPlanInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/plan-status/${planId}/info`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlanInfo(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-gray-500',
      'pending_review': 'bg-yellow-500',
      'in_review': 'bg-blue-500',
      'approved': 'bg-green-500',
      'rejected': 'bg-red-500',
      'locked': 'bg-purple-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-2">Plan Status Timeline</h1>
      {planInfo && (
        <p className="text-gray-600 mb-6">{planInfo.name}</p>
      )}

      {/* Current Status Card */}
      {planInfo && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-gray-600">Current Status</div>
              <div className="text-xl font-bold capitalize">
                {planInfo.lifecycle_status?.replace('_', ' ')}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Version</div>
              <div className="text-xl font-bold">v{planInfo.current_version}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Total Versions</div>
              <div className="text-xl font-bold">{planInfo.total_versions}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Lock Status</div>
              <div className="text-xl font-bold">
                {planInfo.is_locked ? '🔒 Locked' : '🔓 Unlocked'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="relative">
        {history.map((h, idx) => (
          <div key={h.id} className="flex gap-4 mb-6">
            {/* Timeline Line */}
            <div className="flex flex-col items-center">
              <div className={`w-4 h-4 rounded-full ${getStatusColor(h.new_status)}`} />
              {idx < history.length - 1 && (
                <div className="w-0.5 h-16 bg-gray-300" />
              )}
            </div>
            
            {/* Content */}
            <div className="flex-1 pb-8">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-bold capitalize">
                    {h.old_status?.replace('_', ' ') || 'None'} → {h.new_status.replace('_', ' ')}
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(h.changed_at).toLocaleString()}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  Changed by: {h.changed_by?.substring(0, 30)}...
                </div>
                {h.change_reason && (
                  <div className="mt-2 text-sm text-gray-700 italic">
                    "{h.change_reason}"
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {history.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
          No status history yet. Status changes will appear here.
        </div>
      )}
    </div>
  );
};

export default StatusTimeline;